// src/pages/SalesManagement.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  Tooltip,
  MenuItem,
  IconButton,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ListAlt as ListAltIcon,
  NotificationsActive as NotificationsActiveIcon,
  NotificationImportant as NotificationImportantIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { apiUrl } from '../config/api';
import { PAYMENT_ACCOUNT_NAME } from '../config/branding';

// ────────────────────────────── API endpoints ──────────────────────────────
const API_URL             = apiUrl('sales_management');
const CUSTOMERS_API_URL   = apiUrl('get_customers1');
const ADD_CUSTOMER_URL    = apiUrl('add_customer');
const SETTLEMENTS_API_URL = apiUrl('credit_settlements');
const REMINDERS_API_URL   = apiUrl('credit_reminders');
const HISTORY_API_URL     = apiUrl('credit_customer_history');
const CLOSE_CYCLE_URL     = apiUrl('credit_close'); // NEW: close open settlements
const SMS_API             = apiUrl('send_sms');
const CREDIT_PAYMENT_LINK_API_URL = apiUrl('credit_payment_link');

// ────────────────────────────── helpers ──────────────────────────────
const paymentMethodOptions = [
  { label: "Cash",   value: "cash"   },
  { label: "Card",   value: "card"   },
  { label: "Online", value: "online" },
];
const paymentStatusOptions = [
  { label: "Credit",    value: "Credit"    },
  { label: "Paid",      value: "paid"      },
  { label: "Cancelled", value: "cancelled" },
];
const fmt = (n) => Number(n || 0).toFixed(2);
const toArray = (value) => (Array.isArray(value) ? value : []);
const extractList = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  return [];
};

// ────────────────────────────── component ──────────────────────────────
export default function SalesManagement() {
  const navigate = useNavigate();

  // date filters
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo,   setFilterTo]   = useState('');

  // core data
  const [sales,        setSales]        = useState([]);
  const [customerList, setCustomerList] = useState([]);

  // add-sale dialog
  const [openModal,   setOpenModal]   = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [saleData,    setSaleData]    = useState({
    order_details:   '',
    amount:          '',
    payment_method:  '',
    payment_status:  'Credit',
    customer_id:     '',
    date:            new Date().toISOString().split('T')[0],
  });

  // add-customer dialog
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ name:'', email:'', phone:'' });

  // customer-wise rows
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rows, setRows]               = useState([]); // [{customer_id,name,credit_total,credit_count,settlement_total,balance,reminder_total}]

  // customer detail dialog
  const [custOpen, setCustOpen]         = useState(false);
  const [custRow, setCustRow]           = useState(null);
  const [custLoad, setCustLoad]         = useState(false);
  const [custSummary, setCustSummary]   = useState(null); // { settlement_total, settlements:[...] } OPEN cycle only
  const [custHistory, setCustHistory]   = useState(null); // full history payload
  const [rcvAmt, setRcvAmt]             = useState('');
  const [rcvDate, setRcvDate]           = useState(new Date().toISOString().split('T')[0]);
  const [rcvNote, setRcvNote]           = useState('');

  // reminders state
  const [sendingCustId, setSendingCustId] = useState(null);
  const [sendingInvId,  setSendingInvId]  = useState(null);
  const [custRemTotal,  setCustRemTotal]  = useState(0);
  const [custRemMap,    setCustRemMap]    = useState(new Map());

  // mark-all-as-paid spinner
  const [markingPaid, setMarkingPaid] = useState(false);

  // ───────────── fetchers ─────────────
  const fetchSales = () => {
    const params = new URLSearchParams();
    if (filterFrom) params.append('date_from', filterFrom);
    if (filterTo)   params.append('date_to',   filterTo);
    return fetch(`${API_URL}?${params}`)
      .then(r => r.json())
      .then((payload) => {
        const list = extractList(payload, ['items', 'sales', 'data']);
        setSales(list);
        return list;
      })
      .catch((err) => {
        console.error(err);
        setSales([]);
        return [];
      });
  };
  const fetchCustomers = () => {
    return fetch(CUSTOMERS_API_URL)
      .then(r => r.json())
      .then((payload) => {
        const list = extractList(payload, ['items', 'customers', 'data']);
        setCustomerList(list);
        return list;
      })
      .catch((err) => {
        console.error(err);
        setCustomerList([]);
        return [];
      });
  };

  const fetchCustomerHistory = (customerId) => {
    const params = new URLSearchParams();
    params.append('customer_id', customerId);
    if (filterFrom) params.append('date_from', filterFrom);
    if (filterTo)   params.append('date_to',   filterTo);
    return fetch(`${HISTORY_API_URL}?${params}`)
      .then(r => r.json())
      .then(h => {
        if (h?.status === 'ok') {
          setCustHistory(h);
        } else {
          setCustHistory(null);
        }
        return h;
      })
      .catch(() => {
        setCustHistory(null);
        return { status: 'error' };
      });
  };

  useEffect(() => {
    fetchSales();
    fetchCustomers();
  }, []);
  useEffect(() => {
    fetchSales();
  }, [filterFrom, filterTo]);

  const safeSales = useMemo(() => toArray(sales), [sales]);
  const safeCustomerList = useMemo(() => toArray(customerList), [customerList]);

  // maps
  const customersById = useMemo(() => {
    const m = new Map();
    safeCustomerList.forEach(c => m.set(String(c.id), c));
    return m;
  }, [safeCustomerList]);
  const getCustomerName  = (id) => customersById.get(String(id))?.name  || id;
  const getCustomerPhone = (id) => customersById.get(String(id))?.phone || '';

  // customers with at least one sale in range
  const customersWithSales = useMemo(() => {
    const s = new Set();
    safeSales.forEach(x => s.add(String(x.customer)));
    return s;
  }, [safeSales]);

  // unpaid totals by customer (status !== 'paid')
  const unpaidByCustomer = useMemo(() => {
    const map = new Map();
    safeSales.forEach(s => {
      const cid = String(s.customer);
      if (!map.has(cid)) {
        map.set(cid, { customer_id: cid, name: getCustomerName(cid), unpaid_total: 0, unpaid_count: 0 });
      }
      const isUnpaid = (s.payment_status || '').toLowerCase() !== 'paid';
      if (isUnpaid) {
        map.get(cid).unpaid_total += parseFloat(s.amount || 0);
        map.get(cid).unpaid_count += 1;
      }
    });
    return map;
  }, [safeSales, customersById]);

  // ───────────── build customer rows (merge credit totals + OPEN settlements + reminders) ─────────────
  const refreshCustomerRows = () => {
    setRowsLoading(true);
    const openParams = new URLSearchParams();
    openParams.append('summary', '1');
    if (filterFrom) openParams.append('date_from', filterFrom);
    if (filterTo)   openParams.append('date_to',   filterTo);
    openParams.append('closed','0'); // show only OPEN-cycle receipts

    Promise.all([
      fetch(`${SETTLEMENTS_API_URL}?${openParams}`).then(r=>r.json()).catch(()=>({status:'error'})),
      fetch(`${REMINDERS_API_URL}?${openParams}`).then(r=>r.json()).catch(()=>({status:'error'}))
    ])
    .then(([sett, rem]) => {
      const settleRows = (sett.status === 'ok' ? toArray(sett.items) : []);
      const remRows    = (rem.status  === 'ok' ? toArray(rem.items)  : []);
      const merged = [];

      customersWithSales.forEach(cid => {
        const unpaid = unpaidByCustomer.get(cid) || { unpaid_total: 0, unpaid_count: 0, name: getCustomerName(cid) };
        const sr     = settleRows.find(x => String(x.customer_id) === String(cid));
        const rr     = remRows.find(x => String(x.customer_id) === String(cid));
        const settlement_total = parseFloat(sr?.settlement_total || 0); // OPEN only
        const credit_total     = unpaid.unpaid_total;                   // unpaid
        const reminder_total   = parseInt(rr?.reminder_count || 0, 10);
        merged.push({
          customer_id: cid,
          name: unpaid.name,
          credit_total,
          credit_count: unpaid.unpaid_count,
          settlement_total,
          balance: credit_total - settlement_total,
          reminder_total,
        });
      });

      merged.sort((a,b) => b.balance - a.balance);
      setRows(merged);
    })
    .catch(() => {
      const merged = Array.from(customersWithSales.values()).map(cid => {
        const u = unpaidByCustomer.get(cid) || { unpaid_total: 0, unpaid_count: 0, name: getCustomerName(cid) };
        return {
          customer_id: cid,
          name: u.name,
          credit_total: u.unpaid_total,
          credit_count: u.unpaid_count,
          settlement_total: 0,
          balance: u.unpaid_total,
          reminder_total: 0,
        };
      }).sort((a,b) => b.balance - a.balance);
      setRows(merged);
    })
    .finally(() => setRowsLoading(false));
  };
  useEffect(() => {
    refreshCustomerRows();
  }, [unpaidByCustomer, customersWithSales, filterFrom, filterTo]); // eslint-disable-line

  // ───────────── Add sale ─────────────
  const openAddSale = () => {
    setSaleData({
      order_details:   '',
      amount:          '',
      payment_method:  '',
      payment_status:  'Credit',
      customer_id:     '',
      date:            new Date().toISOString().split('T')[0],
    });
    setOpenModal(true);
  };
  const closeModal = () => setOpenModal(false);

  const onSaleChange = (e) => {
    const { name, value } = e.target;
    setSaleData(d => ({ ...d, [name]: value }));
  };

  const saveSale = () => {
    setSaving(true);
    const payload = {
      action:         'add',
      order_details:  saleData.order_details,
      amount:         parseFloat(saleData.amount) || 0,
      payment_method: saleData.payment_method,
      payment_status: saleData.payment_status || 'Credit',
      customer:       saleData.customer_id,
      created_at:     saleData.date,
    };
    fetch(API_URL, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(payload),
    })
    .then(r=>r.json())
    .then(data=>{
      setSaving(false);
      if (data.status==='success') {
        fetchSales();
        refreshCustomerRows();
        closeModal();
      } else {
        alert(data.message || 'Save error');
      }
    })
    .catch(err=>{
      setSaving(false);
      alert('Network error');
      console.error(err);
    });
  };

  // ───────────── Add new customer ─────────────
  const addCustomer = () => {
    const { name, phone, email } = newCustomerData;
    if (!name || !phone) return alert("Name & phone required.");
    fetch(ADD_CUSTOMER_URL, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ name, phone, email })
    })
    .then(r=>r.json())
    .then(d=>{
      if (d.status==='success') {
        fetchCustomers();
        setSaleData(s=>({...s, customer_id: d.id}));
        setNewCustomerOpen(false);
      } else {
        alert("Error: "+d.message);
      }
    })
    .catch(console.error);
  };

  // ───────────── Close open settlements (closed=0 → 1) ─────────────
  const closeCycle = async (customerId) => {
    const res = await fetch(CLOSE_CYCLE_URL, {
      method: 'POST',
      headers: { 'Content-Type':'application/json' },
      body: JSON.stringify({ customer_id: customerId })
    }).then(r=>r.json()).catch(()=>({status:'error'}));
    return res;
  };

  // ───────────── Mark all as PAID (manual only) + close open receipts ─────────────
  const bulkMarkPaidForCustomer = async (customerId) => {
    const toPay = safeSales.filter(s =>
      String(s.customer) === String(customerId) &&
      (s.payment_status || '').toLowerCase() !== 'paid'
    );
    if (!toPay.length) {
      alert('No unpaid invoices to mark as paid.');
      return;
    }

    setMarkingPaid(true);
    try {
      // 1) mark all unpaid invoices as PAID
      await Promise.all(toPay.map(sale => {
        const payload = {
          action:         'update',
          id:             sale.id,
          order_details:  sale.order_details,
          amount:         sale.amount,
          payment_method: sale.payment_method,
          payment_status: 'paid',
          customer:       sale.customer,
          created_at:     (sale.created_at || '').split('T')[0],
        };
        return fetch(API_URL, {
          method:'POST',
          headers:{ 'Content-Type':'application/json' },
          body: JSON.stringify(payload)
        }).then(r=>r.json());
      }));

      // 2) close all OPEN settlements for this customer (closed=0 → 1)
      const closed = await closeCycle(customerId);
      if (closed?.status !== 'ok') {
        // don't fail the whole action—just let the user know
        alert('Invoices marked PAID, but failed to close receipts.');
      }

      // 3) refresh UI (top + dialog)
      await fetchSales();
      await refreshCustomerRows();

      if (custRow) {
        const params = new URLSearchParams();
        params.append('customer_id', custRow.customer_id);
        if (filterFrom) params.append('date_from', filterFrom);
        if (filterTo)   params.append('date_to',   filterTo);
        params.append('closed','0');
        const s = await fetch(`${SETTLEMENTS_API_URL}?${params}`).then(r=>r.json()).catch(()=>({status:'error'}));
        setCustSummary(s.status==='ok'? s : { settlement_total: 0, settlements: [] });
        await fetchCustomerHistory(custRow.customer_id);
      }

      alert('All unpaid invoices marked as PAID and open receipts closed.');
    } catch (e) {
      alert('Failed to mark invoices as paid.');
    } finally {
      setMarkingPaid(false);
    }
  };

  // ───────────── open customer dialog (fetch OPEN-cycle settlements + reminders) ─────────────
  const openCustomer = (row) => {
    const params = new URLSearchParams();
    if (filterFrom) params.append('date_from', filterFrom);
    if (filterTo)   params.append('date_to',   filterTo);
    const qs = params.toString();
    navigate(
      `/sales/customer/${row.customer_id}${qs ? `?${qs}` : ''}`,
      { state: { customerName: row.name } }
    );
  };
  const closeCustomer = () => {
    setCustOpen(false);
    setCustRow(null);
    setCustSummary(null);
    setCustHistory(null);
  };

  // derived for selected customer
  const customerUnpaid = useMemo(() => {
    if (!custRow) return [];
    return safeSales.filter(s =>
      String(s.customer) === String(custRow.customer_id) &&
      (s.payment_status || '').toLowerCase() !== 'paid'
    );
  }, [custRow, safeSales]);
  const custCreditTotal     = useMemo(() => customerUnpaid.reduce((sum, s) => sum + parseFloat(s.amount || 0), 0), [customerUnpaid]);
  const custSettlementTotal = parseFloat(custSummary?.settlement_total || 0); // OPEN cycle only
  const custBalance         = custCreditTotal - custSettlementTotal;

  // ───────────── add settlement (receive amount) ─────────────
  const addSettlement = () => {
    if (!custRow) return;
    const amt = parseFloat(rcvAmt);
    if (!amt || amt <= 0) return alert('Enter a valid amount');
    const payload = {
      action: 'add',
      customer_id: custRow.customer_id,
      amount: amt,
      pay_date: rcvDate,
      note: rcvNote
    };
    fetch(SETTLEMENTS_API_URL, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(payload)
    })
    .then(r=>r.json())
    .then(d => {
      if (d.status !== 'ok') return alert(d.message || 'Failed to add settlement');
      if (d.auto_closed) {
        const c = d.closure || {};
        alert(
          `Customer fully settled.\n` +
          `Credit: Rs ${fmt(c.open_credit_total)}\n` +
          `Received: Rs ${fmt(c.received_total)}\n` +
          `Invoices Closed: ${c.paid_invoices || 0}\n` +
          `Settlement SMS: ${c.sms_sent ? 'Sent' : 'Not Sent'}`
        );
      }
      // refresh per-customer summary + top table (OPEN cycle only)
      const params = new URLSearchParams();
      params.append('customer_id', custRow.customer_id);
      if (filterFrom) params.append('date_from', filterFrom);
      if (filterTo)   params.append('date_to',   filterTo);
      params.append('closed','0');
      return fetch(`${SETTLEMENTS_API_URL}?${params}`)
        .then(r=>r.json())
        .then(s => setCustSummary(s.status==='ok'? s : { settlement_total: 0, settlements: [] }))
        .then(() => {
          refreshCustomerRows();
          return fetchCustomerHistory(custRow.customer_id);
        });
    })
    .then(() => {
      setRcvAmt('');
      setRcvNote('');
    })
    .catch(() => alert('Network error'));
  };

  // ───────────── Reminders (SMS + logging) ─────────────
  const sendSMS = (to, text) =>
    fetch(`${SMS_API}?to=${encodeURIComponent(to)}&text=${encodeURIComponent(text)}`)
      .then(r => r.text());

  const fetchPaymentLink = async (customerId, saleId = null) => {
    const params = new URLSearchParams();
    params.append('customer_id', String(customerId));
    if (saleId != null) {
      params.append('sale_id', String(saleId));
    }
    const d = await fetch(`${CREDIT_PAYMENT_LINK_API_URL}?${params.toString()}`)
      .then(r => r.json())
      .catch(() => ({ status: 'error' }));
    if (d?.status !== 'ok' || !d?.payment_link) {
      throw new Error(d?.message || 'Failed to generate payment link');
    }
    return String(d.payment_link);
  };

  // helper: log reminder to backend then refresh counts
  const logReminder = (customer_id, sale_id, message, api_response) =>
    fetch(REMINDERS_API_URL, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({
        action: 'add',
        customer_id,
        sale_id: sale_id ?? null,
        channel: 'sms',
        message,
        api_response
      })
    }).then(r=>r.json()).catch(()=>({status:'error'}));

  // Customer-level reminder (total balance)
  const remindCustomerTotal = async (row) => {
    const phone = getCustomerPhone(row.customer_id);
    if (!phone) return alert('No phone on record for this customer.');
    const count = row.credit_count || 0;
    try {
      setSendingCustId(row.customer_id);
      const paymentLink = await fetchPaymentLink(row.customer_id);
      const msg =
        `Dear ${row.name}, your outstanding balance is Rs.${fmt(row.balance)} ` +
        `for ${count} invoice(s). Please settle at your earliest convenience.\n` +
        `Pay online securely:\n${paymentLink}\n` +
        `Thank you - ${PAYMENT_ACCOUNT_NAME}!`;
      const resp = await sendSMS(phone, msg);
      await logReminder(row.customer_id, null, msg, resp);
      // refresh counts (list + dialog if open for same customer)
      refreshCustomerRows();
      if (custOpen && custRow && String(custRow.customer_id) === String(row.customer_id)) {
        const params = new URLSearchParams();
        params.append('customer_id', row.customer_id);
        if (filterFrom) params.append('date_from', filterFrom);
        if (filterTo)   params.append('date_to',   filterTo);
        const rem = await fetch(`${REMINDERS_API_URL}?${params}`).then(r=>r.json()).catch(()=>({status:'error'}));
        if (rem.status === 'ok') {
          setCustRemTotal(rem.total || 0);
          const m = new Map();
          toArray(rem.items).forEach(it => {
            const key = it.sale_id === null ? 'customer' : String(it.sale_id);
            m.set(key, it.count || 0);
          });
          setCustRemMap(m);
        }
        fetchCustomerHistory(row.customer_id);
      }
      alert(`Reminder sent. Response: ${resp}`);
    } catch (e) {
      alert('Failed to send SMS');
    } finally {
      setSendingCustId(null);
    }
  };

  // Invoice-level reminder (single invoice within dialog)
  const remindInvoice = async (inv) => {
    const phone = getCustomerPhone(inv.customer);
    const custName = getCustomerName(inv.customer);
    if (!phone) return alert('No phone on record for this customer.');
    const invNo = inv.order_details || inv.id;
    const date  = (inv.created_at || '').split('T')[0] || '';
    try {
      setSendingInvId(inv.id);
      const paymentLink = await fetchPaymentLink(inv.customer, inv.id);
      const msg =
        `Dear ${custName}, a kind reminder to clear Rs.${fmt(inv.amount)} ` +
        `(Invoice #${invNo}, ${date}).\n` +
        `Pay online securely:\n${paymentLink}\n` +
        `Please note: invoice no. ${invNo}.`;
      const resp = await sendSMS(phone, msg);
      await logReminder(inv.customer, inv.id, msg, resp);
      // refresh counts in dialog + list
      refreshCustomerRows();
      if (custOpen) {
        const params = new URLSearchParams();
        params.append('customer_id', inv.customer);
        if (filterFrom) params.append('date_from', filterFrom);
        if (filterTo)   params.append('date_to',   filterTo);
        const rem = await fetch(`${REMINDERS_API_URL}?${params}`).then(r=>r.json()).catch(()=>({status:'error'}));
        if (rem.status === 'ok') {
          setCustRemTotal(rem.total || 0);
          const m = new Map();
          toArray(rem.items).forEach(it => {
            const key = it.sale_id === null ? 'customer' : String(it.sale_id);
            m.set(key, it.count || 0);
          });
          setCustRemMap(m);
        }
        fetchCustomerHistory(inv.customer);
      }
      alert(`Reminder sent. Response: ${resp}`);
    } catch (e) {
      alert('Failed to send SMS');
    } finally {
      setSendingInvId(null);
    }
  };

  // ───────────── UI ─────────────
  return (
    <Box sx={{ p:2 }}>
      <Typography variant="h4" gutterBottom>Credit Management</Typography>

      {/* Filters */}
      <Box sx={{ display:'flex', gap:2, mb:2, flexWrap:'wrap', alignItems:'center' }}>
        <TextField
          label="From Date" type="date" size="small"
          InputLabelProps={{ shrink:true }}
          value={filterFrom}
          onChange={e=>setFilterFrom(e.target.value)}
        />
        <TextField
          label="To Date" type="date" size="small"
          InputLabelProps={{ shrink:true }}
          value={filterTo}
          onChange={e=>setFilterTo(e.target.value)}
        />
        <Button
          variant="contained"
          startIcon={<RefreshIcon/>}
          onClick={() => { fetchSales(); refreshCustomerRows(); }}
        >
          Apply Filter
        </Button>
        <Box flexGrow={1}/>
        <Button
          variant="contained"
          startIcon={<AddIcon/>}
          onClick={openAddSale}
        >
          Add New Sale
        </Button>
      </Box>

      {/* Customer-wise credits */}
      <Paper sx={{ p:2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb:1 }}>
          <ListAltIcon fontSize="small" />
          <Typography variant="h6">Customer-wise Credits</Typography>
          <Box flexGrow={1}/>
          {rowsLoading && <CircularProgress size={18} />}
        </Stack>
        <Divider sx={{ mb:1 }}/>
        {rows.length === 0 ? (
          <Typography color="text.secondary">
            No customers found for this date range.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={64}>No.</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Unpaid Count</TableCell>
                <TableCell align="right">Credit Total (Unpaid)</TableCell>
                <TableCell align="right">Received (Open Cycle)</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="center">Reminders</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, index) => (
                <TableRow key={r.customer_id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>{r.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{r.credit_count || 0}</TableCell>
                  <TableCell align="right">Rs {fmt(r.credit_total)}</TableCell>
                  <TableCell align="right" style={{ color:'#2e7d32' }}>Rs {fmt(r.settlement_total)}</TableCell>
                  <TableCell align="right" style={{ fontWeight:700, color: (r.balance||0) > 0 ? '#f57c00' : '#2e7d32' }}>
                    Rs {fmt(r.balance)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" variant="outlined" label={`x${r.reminder_total || 0}`} />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Remind Customer">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              sendingCustId === r.customer_id
                                ? <CircularProgress size={16}/>
                                : <NotificationsActiveIcon fontSize="small"/>
                            }
                            onClick={() => remindCustomerTotal(r)}
                            disabled={sendingCustId === r.customer_id}
                          >
                            Remind
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title="Open Details">
                        <Button size="small" variant="outlined" onClick={() => openCustomer(r)}>
                          Open Page
                        </Button>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Add New Sale Dialog */}
      <Dialog open={openModal} onClose={closeModal} fullWidth>
        <DialogTitle>Record New Sale</DialogTitle>
        <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          <TextField
            label="Date"
            type="date"
            name="date"
            InputLabelProps={{ shrink:true }}
            value={saleData.date}
            onChange={onSaleChange}
            variant="standard"
            fullWidth
          />

          <Autocomplete
            options={safeCustomerList}
            getOptionLabel={c=>`${c.name} (${c.phone})`}
            value={safeCustomerList.find(c=>String(c.id)===String(saleData.customer_id)) || null}
            onChange={(_,v)=>setSaleData(d=>({...d,customer_id:v?.id||''}))}
            renderInput={params=>(
              <TextField {...params} label="Customer" variant="standard" fullWidth />
            )}
          />
          <Box sx={{ textAlign:'right', mb:1 }}>
            <Button size="small" variant="text" onClick={()=>setNewCustomerOpen(true)}>
              + Add New Customer
            </Button>
          </Box>

          <TextField
            name="order_details"
            label="Order Ref. Number"
            variant="standard"
            fullWidth
            value={saleData.order_details}
            onChange={onSaleChange}
          />
          <TextField
            name="amount"
            label="Amount"
            type="number"
            variant="standard"
            fullWidth
            value={saleData.amount}
            onChange={onSaleChange}
          />
          <TextField
            select
            name="payment_method"
            label="Payment Method"
            variant="standard"
            fullWidth
            value={saleData.payment_method}
            onChange={onSaleChange}
          >
            {paymentMethodOptions.map(o=>(
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          {/* default Credit */}
          <TextField
            select
            name="payment_status"
            label="Payment Status"
            variant="standard"
            fullWidth
            value={saleData.payment_status}
            onChange={onSaleChange}
          >
            {paymentStatusOptions.map(o=>(
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button
            onClick={saveSale}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer Details Dialog */}
      <Dialog open={custOpen} onClose={closeCustomer} maxWidth="md" fullWidth>
        <DialogTitle>
          {custRow ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', justifyContent: 'space-between' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6">{custRow.name}</Typography>
                <Chip size="small" variant="outlined" label={`reminders x${custRemTotal}`} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Mark ALL unpaid invoices as PAID & close open receipts">
                  <span>
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      startIcon={markingPaid ? <CircularProgress size={16} /> : <DoneAllIcon fontSize="small" />}
                      onClick={() => bulkMarkPaidForCustomer(custRow.customer_id)}
                      disabled={markingPaid || (customerUnpaid.length === 0)}
                    >
                      Mark All as Paid
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Send balance reminder">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        sendingCustId === custRow.customer_id
                          ? <CircularProgress size={16}/>
                          : <NotificationsActiveIcon fontSize="small"/>
                      }
                      onClick={() => remindCustomerTotal({
                        ...custRow,
                        credit_total: custCreditTotal,
                        settlement_total: custSettlementTotal,
                        balance: custBalance,
                        credit_count: customerUnpaid.length
                      })}
                      disabled={sendingCustId === custRow.customer_id}
                    >
                      Remind
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          ) : 'Customer'}
        </DialogTitle>
        <DialogContent dividers sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          {custLoad ? (
            <Box sx={{ textAlign:'center', py:3 }}><CircularProgress/></Box>
          ) : (
            <>
              {/* Summary */}
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Paper sx={{ p:2, minWidth: 220 }}>
                  <Typography variant="subtitle2">Unpaid Credits</Typography>
                  <Typography variant="h6">Rs {fmt(custCreditTotal)}</Typography>
                </Paper>
                <Paper sx={{ p:2, minWidth: 220 }}>
                  <Typography variant="subtitle2">Received (Open Cycle)</Typography>
                  <Typography variant="h6" color="success.main">Rs {fmt(custSettlementTotal)}</Typography>
                </Paper>
                <Paper sx={{ p:2, minWidth: 220 }}>
                  <Typography variant="subtitle2">Balance</Typography>
                  <Typography variant="h6" color={(custBalance||0) > 0 ? 'warning.main' : 'success.main'}>
                    Rs {fmt(custBalance)}
                  </Typography>
                </Paper>
                <Paper sx={{ p:2, minWidth: 220 }}>
                  <Typography variant="subtitle2">Received (All History)</Typography>
                  <Typography variant="h6" color="info.main">
                    Rs {fmt(custHistory?.summary?.settlement_total || 0)}
                  </Typography>
                </Paper>
                <Paper sx={{ p:2, minWidth: 220 }}>
                  <Typography variant="subtitle2">Auto Closures</Typography>
                  <Typography variant="h6">
                    {custHistory?.summary?.auto_closure_count || 0}
                  </Typography>
                </Paper>
              </Stack>

              {/* Receive Amount */}
              <Paper sx={{ p:2 }}>
                <Typography variant="subtitle1" gutterBottom>Receive Amount</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
                  <TextField
                    label="Amount"
                    type="number"
                    value={rcvAmt}
                    onChange={e=>setRcvAmt(e.target.value)}
                    size="small"
                  />
                  <TextField
                    label="Date"
                    type="date"
                    value={rcvDate}
                    onChange={e=>setRcvDate(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink:true }}
                  />
                  <TextField
                    label="Note (optional)"
                    value={rcvNote}
                    onChange={e=>setRcvNote(e.target.value)}
                    size="small"
                    sx={{ minWidth: 240 }}
                  />
                  <Button variant="contained" onClick={addSettlement}>
                    Add
                  </Button>
                </Stack>
              </Paper>

              {/* Unpaid Invoices */}
              <Typography variant="subtitle1">Unpaid Invoices</Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={64}>No.</TableCell>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Reminder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerUnpaid.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No unpaid invoices.</TableCell></TableRow>
                    ) : customerUnpaid.map((inv, index) => (
                      <TableRow key={inv.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{inv.order_details}</TableCell>
                        <TableCell>{(inv.created_at||'').split('T')[0]}</TableCell>
                        <TableCell>
                          <Chip size="small" label="Credit" color="warning" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">Rs {fmt(inv.amount)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                            <Tooltip title="Send Reminder">
                              <span>
                                <IconButton
                                  onClick={() => remindInvoice(inv)}
                                  disabled={sendingInvId === inv.id}
                                  size="small"
                                  color="warning"
                                >
                                  {sendingInvId === inv.id
                                    ? <CircularProgress size={18}/>
                                    : <NotificationImportantIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`x${custRemMap.get(String(inv.id)) || 0}`}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* Settlement History (open cycle) */}
              <Typography variant="subtitle1">Settlement History (Open Cycle)</Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={64}>No.</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(custSummary?.settlements || []).length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center">No settlements yet.</TableCell></TableRow>
                    ) : (custSummary?.settlements || []).map((s, index) => (
                      <TableRow key={s.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{s.pay_date}</TableCell>
                        <TableCell align="right">Rs {fmt(s.amount)}</TableCell>
                        <TableCell>{s.note}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* Full Settlement History */}
              <Typography variant="subtitle1">Settlement History (All)</Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={64}>No.</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(custHistory?.settlements?.all || []).length === 0 ? (
                      <TableRow><TableCell colSpan={5} align="center">No settlement history.</TableCell></TableRow>
                    ) : (custHistory?.settlements?.all || []).map((s, index) => (
                      <TableRow key={`all-${s.id}`}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{s.pay_date}</TableCell>
                        <TableCell align="right">Rs {fmt(s.amount)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={Number(s.closed) === 1 ? 'success' : 'warning'}
                            variant="outlined"
                            label={Number(s.closed) === 1 ? 'Closed Cycle' : 'Open Cycle'}
                          />
                        </TableCell>
                        <TableCell>{s.note || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* Auto-close logs */}
              <Typography variant="subtitle1">Settlement Closure Logs</Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={64}>No.</TableCell>
                      <TableCell>Closed At</TableCell>
                      <TableCell align="right">Credit</TableCell>
                      <TableCell align="right">Received</TableCell>
                      <TableCell align="right">Overpaid</TableCell>
                      <TableCell>SMS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(custHistory?.closure_logs || []).length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No closure logs.</TableCell></TableRow>
                    ) : (custHistory?.closure_logs || []).map((l, index) => (
                      <TableRow key={`log-${l.id}`}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{l.closed_at}</TableCell>
                        <TableCell align="right">Rs {fmt(l.open_credit_total)}</TableCell>
                        <TableCell align="right">Rs {fmt(l.received_total)}</TableCell>
                        <TableCell align="right">Rs {fmt(l.overpaid)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={Number(l.sms_sent) === 1 ? 'success' : 'default'}
                            variant="outlined"
                            label={Number(l.sms_sent) === 1 ? 'Sent' : 'Not Sent'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>

              {/* Ledger timeline */}
              <Typography variant="subtitle1">Customer Ledger Timeline</Typography>
              <Paper variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={64}>No.</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(custHistory?.ledger || []).length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center">No ledger activity.</TableCell></TableRow>
                    ) : (custHistory?.ledger || []).slice(-50).map((e, idx) => (
                      <TableRow key={`ledger-${idx}-${e.type}-${e.sale_id || e.settlement_id || ''}`}>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{e.date}</TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={e.type === 'invoice' ? 'Invoice' : 'Settlement'} />
                        </TableCell>
                        <TableCell align="right">Rs {fmt(e.amount)}</TableCell>
                        <TableCell>{e.status || '-'}</TableCell>
                        <TableCell>{e.reference || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCustomer}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
